import { useContext, useEffect, useState } from 'react'
import { Bars3Icon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { Navigate, Outlet } from 'libs/router'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import { ProfileChip, Sidebar } from 'shared/components'
import { IconButton } from 'shared/atoms'
import { DashboardContext, StoreSettingsContext, NavContext, PageHeaderContext } from 'store'
import { defaultConfig } from 'config/default'
import { cn } from 'utils/style'
import { useFetchStoreSettings } from './queries/useFetchStoreSettings'
import { useFetchWidgetData } from './queries/useFetchWidgetData'
import { useFetchDashboardData } from './queries/useFetchDashboardData'

const Dashboard = () => {
	const { t } = useTranslation()
	const [pageHeader, setPageHeader] = useState<HTMLDivElement | null>(null)
	const [pageHeaderTabs, setPageHeaderTabs] = useState<HTMLDivElement | null>(null)
	const [hasTabs, setHasTabs] = useState(false)
	const navCtx = useContext(NavContext)
	const storeCtx = useContext(StoreSettingsContext)
	const dashboardCtx = useContext(DashboardContext)
	const isNavOpened = !!navCtx?.isNavOpened
	const isRefreshToken = localStorage.getItem('refresh')

	const { storeSettings, isStoreSettingsSuccess } = useFetchStoreSettings()
	useEffect(() => {
		if (isStoreSettingsSuccess) {
			storeCtx?.setStoreSettings(storeSettings?.data)

			refetchWidgetData()
		}
	}, [storeSettings, isStoreSettingsSuccess])

	const { widgetData, isWidgetDataSuccess, refetchWidgetData } = useFetchWidgetData()
	useEffect(() => {
		if (isWidgetDataSuccess) {
			storeCtx?.setPrimaryColor(widgetData?.data?.primary_color || '#EF6C00')
		}
	}, [widgetData, isWidgetDataSuccess])

	const { dashboardData, isDashboardDataSuccess } = useFetchDashboardData()
	useEffect(() => {
		if (isDashboardDataSuccess) {
			dashboardCtx?.setDashboard(dashboardData?.data ?? null)
		}
	}, [dashboardData, isDashboardDataSuccess])

	useEffect(() => {
		if (!isNavOpened) return

		document.body.style.overflow = 'hidden'

		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isNavOpened])

	return isRefreshToken ? (
		<section className="flex min-h-screen flex-col bg-background xl:pl-64">
			<PageHeaderContext.Provider value={{ main: pageHeader, tabs: pageHeaderTabs, setHasTabs }}>
				<header className="sticky top-0 z-30 shrink-0 border-b border-border bg-card/90 backdrop-blur">
					<div
						className={cn(
							'flex items-center gap-3 px-5 lg:px-8',
							hasTabs ? 'h-[61px] pt-4' : 'h-[71px]'
						)}>
						<IconButton
							className="xl:hidden"
							aria-label={t('Open menu')}
							onClick={() => navCtx?.toggleNavOpened(true)}>
							<Bars3Icon />
						</IconButton>
						<div
							ref={setPageHeader}
							className="flex min-w-0 flex-1 items-center justify-between gap-4"
						/>
						<ProfileChip className="hidden sm:flex" />
					</div>
					<div ref={setPageHeaderTabs} className="pt-3 empty:hidden" />
				</header>
				<section className="max-w-full flex-1 pb-4 pt-5 lg:p-8">
					<Outlet />
				</section>
			</PageHeaderContext.Provider>
			<Sidebar />
			<ToastContainer
				position="bottom-center"
				autoClose={defaultConfig.redirectTimer}
				rtl={false}
				hideProgressBar
				closeOnClick={false}
			/>
		</section>
	) : (
		<Navigate to="/login" />
	)
}

export default Dashboard
